import React, { useState, useEffect } from 'react';
import socketService from '../services/socketService';
import Layout from '../components/Layout';

const Dashboard = () => {
    const [currentTime, setCurrentTime] = useState(new Date());
    
    // ফিল্টারের জন্য স্টেট
    const [selectedDate, setSelectedDate] = useState(''); 
    const [appliedDate, setAppliedDate] = useState('');   
    const [isFilterLoading, setIsFilterLoading] = useState(false); 

    const [stats, setStats] = useState({
        totalUsers: 0,
        totalDevices: 0,
        today: { totalWithdraw: 0, bkashTotal: 0, nagadTotal: 0 },
        yesterday: { totalWithdraw: 0, bkashTotal: 0, nagadTotal: 0 },
        lifetime: { totalWithdraw: 0, bkashTotal: 0, nagadTotal: 0 },
        lastMonth: { totalWithdraw: 0, bkashTotal: 0, nagadTotal: 0 },
        currentMonth: { totalWithdraw: 0, bkashTotal: 0, nagadTotal: 0 },
        depositToday: { totalDeposit: 0, bkashTotal: 0, nagadTotal: 0 },
        depositYesterday: { totalDeposit: 0, bkashTotal: 0, nagadTotal: 0 },
        depositLifetime: { totalDeposit: 0, bkashTotal: 0, nagadTotal: 0 },
        depositLastMonth: { totalDeposit: 0, bkashTotal: 0, nagadTotal: 0 },
        depositCurrentMonth: { totalDeposit: 0, bkashTotal: 0, nagadTotal: 0 },
        customWithdraw: { totalWithdraw: 0, bkashTotal: 0, nagadTotal: 0 }, 
        customDeposit: { totalDeposit: 0, bkashTotal: 0, nagadTotal: 0 },  
        loading: true,
        error: null,
    });

    const adminId = localStorage.getItem('adminId');

    const logos = {
        bkash: "https://main.bdsoftpay.live/bkash.jpg",
        nagad: "https://main.bdsoftpay.live/nagad.png"
    };

    // লাইভ ঘড়ির জন্য
    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    // Helper function for auth payload
    const getCommonPayload = () => {
        let allowedUsers = [];
        try {
            const stored = localStorage.getItem('allowedUsers');
            allowedUsers = stored ? JSON.parse(stored) : [];
        } catch (e) { allowedUsers = []; }
        return {
            isMaster: localStorage.getItem('isMaster'),
            masterKey: localStorage.getItem('masterKey'),
            allowedUsers
        };
    };

    // ==========================================
    // Main Dashboard & Socket Listeners Setup
    // ==========================================
    useEffect(() => {
        let isMounted = true; // মেমরি লিক ঠেকানোর জন্য
        let retryCount = 0;   // রিট্রাই কাউন্টার

        const handleMainData = (data) => {
            if (!isMounted) return;
            setStats(prev => ({
                ...prev,
                totalUsers: data.total_users || 0,
                totalDevices: data.total_devices || 0,
                today: data.today || prev.today,
                yesterday: data.yesterday || prev.yesterday,
                lifetime: data.lifetime || prev.lifetime,
                lastMonth: data.lastMonth || prev.lastMonth,
                currentMonth: data.currentMonth || prev.currentMonth,
                depositToday: data.depositToday || prev.depositToday,
                depositYesterday: data.depositYesterday || prev.depositYesterday,
                depositLifetime: data.depositLifetime || prev.depositLifetime,
                depositLastMonth: data.depositLastMonth || prev.depositLastMonth,
                depositCurrentMonth: data.depositCurrentMonth || prev.depositCurrentMonth,
                loading: false,
                error: null
            }));
            retryCount = 0; // ডাটা পেয়ে গেলে রিট্রাই রিসেট হবে
        };

        const handleFilteredData = (data) => {
            if (!isMounted) return;
            setStats(prev => ({
                ...prev,
                customWithdraw: data.customWithdraw || { totalWithdraw: 0, bkashTotal: 0, nagadTotal: 0 },
                customDeposit: data.customDeposit || { totalDeposit: 0, bkashTotal: 0, nagadTotal: 0 }
            }));
            setIsFilterLoading(false); 
        };

        const handleError = (msg) => {
            if (!isMounted) return;
            
            // 🔥 সার্ভার Unauthorized বললে অটোমেটিক রিট্রাই করবে (৩ বার পর্যন্ত)
            if (msg && msg.toLowerCase().includes('unauthorized') && retryCount < 3) {
                retryCount++;
                console.log(`🔄 Session lost. Retrying fetch data... (Attempt ${retryCount})`);
                if (adminId) {
                    socketService.emit('register-admin-socket', { adminId });
                    setTimeout(() => {
                        socketService.emit('fetch-dashboard-data', getCommonPayload());
                    }, 500);
                }
            } else {
                setStats(prev => ({ ...prev, loading: false, error: msg }));
                setIsFilterLoading(false);
            }
        };

        const requestData = () => {
            if (adminId) {
                socketService.emit('register-admin-socket', { adminId });
                setTimeout(() => {
                    socketService.emit('fetch-dashboard-data', getCommonPayload());
                }, 300);
            } else {
                if(isMounted) setStats(prev => ({ ...prev, loading: false, error: "Admin not found." }));
            }
        };

        // ১. ইভেন্ট লিসেনার রেজিস্ট্রেশন
        socketService.on('dashboard-data-response', handleMainData);
        socketService.on('filtered-data-response', handleFilteredData);
        socketService.on('dashboard-error', handleError);
        socketService.on('connect', requestData); // নেটওয়ার্ক ড্রপ হলে আবার ফেচ করবে

        // ২. কম্পোনেন্ট লোড হওয়া মাত্রই রিকোয়েস্ট (অন্য পেজ থেকে ফিরে আসার জন্য)
        requestData();

        // ৩. সকেট কানেক্ট না থাকলে কানেক্ট করার ট্রাই করবে
        if (typeof socketService.connect === 'function') {
            socketService.connect();
        }

        return () => {
            isMounted = false;
            socketService.off('dashboard-data-response', handleMainData);
            socketService.off('filtered-data-response', handleFilteredData);
            socketService.off('dashboard-error', handleError);
            socketService.off('connect', requestData);
        };
    }, [adminId]);

    // ==========================================
    // Search Button Click Handler
    // ==========================================
    const handleSearch = () => {
        if (!selectedDate) return;
        
        setAppliedDate(selectedDate); 
        setIsFilterLoading(true);     
        
        const payload = { ...getCommonPayload(), customDate: selectedDate };
        socketService.emit('fetch-filtered-data', payload);
    };

    // ==========================================
    // Clear Filter Handler
    // ==========================================
    const handleClearFilter = () => {
        setSelectedDate('');
        setAppliedDate('');
        setStats(prev => ({
            ...prev,
            customWithdraw: { totalWithdraw: 0, bkashTotal: 0, nagadTotal: 0 },
            customDeposit: { totalDeposit: 0, bkashTotal: 0, nagadTotal: 0 }
        }));
    };

    const styles = {
        bkashColor: '#E2136E',
        nagadColor: '#F7921C',
        gradientBlue: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
        gradientInfo: 'linear-gradient(135deg, #17a2b8 0%, #117a8b 100%)',
        depositTheme: '#198754'
    };

    if (stats.loading) {
        return (
            <Layout>
                <div className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
                    <div className="spinner-grow text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </Layout>
        );
    }

    if (stats.error) {
        return (
            <Layout>
                <div className="alert alert-danger m-4 shadow" role="alert">
                    <strong>Error:</strong> {stats.error}
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="container-fluid py-4">
                {/* টপ হেডার, ঘড়ি এবং ডেট ফিল্টার (সার্চ বাটন সহ) */}
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center border-bottom pb-3 mb-4 gap-3">
                    <div className="d-flex flex-wrap align-items-center gap-3">
                        <h2 className="mb-0 fw-bold text-dark">📊 Dashboard</h2>
                        
                        <div className="input-group shadow-sm" style={{ width: 'auto' }}>
                            <input 
                                type="date" 
                                className="form-control" 
                                style={{ border: '2px solid #007bff', borderRight: 'none', cursor: 'pointer', fontWeight: 'bold' }}
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                                title="Select a Date"
                            />
                            <button 
                                className="btn btn-primary fw-bold px-4" 
                                onClick={handleSearch}
                                disabled={!selectedDate || isFilterLoading}
                                style={{ border: '2px solid #007bff' }}
                            >
                                {isFilterLoading ? (
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                ) : (
                                    <><i className="bi bi-search me-1"></i> Search</>
                                )}
                            </button>
                        </div>
                    </div>

                    <div className="text-secondary fw-bold px-3 py-2 bg-light rounded shadow-sm" style={{ border: '1px solid #dee2e6' }}>
                        <i className="bi bi-clock me-2"></i>
                        {currentTime.toLocaleString('en-US', { 
                            weekday: 'long', year: 'numeric', month: 'short', day: 'numeric', 
                            hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true 
                        })}
                    </div>
                </div>

                {/* কাস্টম ডেট ফিল্টার সেকশন (শুধুমাত্র সার্চ করার পর শো করবে) */}
                {appliedDate && (
                    <div className="mb-5 animate__animated animate__fadeIn">
                        <div className="alert alert-primary shadow-sm d-flex justify-content-between align-items-center mb-4">
                            <div>
                                <h5 className="mb-1 fw-bold">
                                    <i className="bi bi-calendar-check me-2"></i> 
                                    Filtered Stats for: {new Date(appliedDate).toLocaleDateString('en-GB')}
                                </h5>
                                <p className="mb-0 small text-dark">Showing total Deposit and Withdraw only for the selected date.</p>
                            </div>
                            <button className="btn btn-outline-danger btn-sm fw-bold shadow-sm" onClick={handleClearFilter}>
                                <i className="bi bi-x-circle me-1"></i> Clear Filter
                            </button>
                        </div>
                        
                        <div className="row g-4">
                            <div className="col-md-6">
                                <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '15px', borderLeft: `6px solid ${styles.depositTheme}` }}>
                                    <div className="card-body p-4">
                                        <h6 className="text-success text-uppercase mb-3 fw-bold">📥 Date Filtered Deposit</h6>
                                        {isFilterLoading ? (
                                            <div className="d-flex align-items-center text-muted">
                                                <div className="spinner-grow spinner-grow-sm me-2" role="status"></div> Loading...
                                            </div>
                                        ) : (
                                            <>
                                                <h2 className="fw-bold text-dark mb-3">৳ {stats.customDeposit.totalDeposit.toLocaleString()}</h2>
                                                <div className="d-flex justify-content-between small text-muted border-top pt-2">
                                                    <span>Bkash: <b>৳ {stats.customDeposit.bkashTotal.toLocaleString()}</b></span>
                                                    <span>Nagad: <b>৳ {stats.customDeposit.nagadTotal.toLocaleString()}</b></span>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '15px', borderLeft: '6px solid #0d6efd' }}>
                                    <div className="card-body p-4">
                                        <h6 className="text-primary text-uppercase mb-3 fw-bold">💰 Date Filtered Withdraw</h6>
                                        {isFilterLoading ? (
                                            <div className="d-flex align-items-center text-muted">
                                                <div className="spinner-grow spinner-grow-sm me-2" role="status"></div> Loading...
                                            </div>
                                        ) : (
                                            <>
                                                <h2 className="fw-bold text-dark mb-3">৳ {stats.customWithdraw.totalWithdraw.toLocaleString()}</h2>
                                                <div className="d-flex justify-content-between small text-muted border-top pt-2">
                                                    <span>Bkash: <b>৳ {stats.customWithdraw.bkashTotal.toLocaleString()}</b></span>
                                                    <span>Nagad: <b>৳ {stats.customWithdraw.nagadTotal.toLocaleString()}</b></span>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* 1. Basic Info Cards */}
                <div className="row g-4 mb-5">
                    <div className="col-md-6">
                        <div className="card text-white border-0 shadow-sm h-100" style={{ background: styles.gradientBlue, borderRadius: '15px' }}>
                            <div className="card-body p-4 d-flex align-items-center justify-content-between">
                                <div>
                                    <h6 className="text-uppercase mb-1 opacity-75">Total Users</h6>
                                    <h2 className="display-5 fw-bold mb-0">{stats.totalUsers.toLocaleString()}</h2>
                                </div>
                                <i className="bi bi-people-fill fs-1 opacity-50"></i>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="card text-white border-0 shadow-sm h-100" style={{ background: styles.gradientInfo, borderRadius: '15px' }}>
                            <div className="card-body p-4 d-flex align-items-center justify-content-between">
                                <div>
                                    <h6 className="text-uppercase mb-1 opacity-75">Total Devices</h6>
                                    <h2 className="display-5 fw-bold mb-0">{stats.totalDevices.toLocaleString()}</h2>
                                </div>
                                <i className="bi bi-phone-fill fs-1 opacity-50"></i>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ======================= DEPOSIT OVERVIEW ======================= */}
                <h5 className="mb-3 text-success fw-bold text-uppercase">📥 Deposit Overview</h5>
                <div className="row g-4 mb-5">
                    <div className="col-lg-4 col-md-6">
                        <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '15px', borderLeft: `6px solid ${styles.depositTheme}` }}>
                            <div className="card-body p-4 d-flex flex-column justify-content-center">
                                <h6 className="text-success text-uppercase mb-2 fw-bold">Running Month Deposit</h6>
                                <h3 className="fw-bold text-dark mb-0">৳ {stats.depositCurrentMonth.totalDeposit.toLocaleString()}</h3>
                                <small className="text-muted mt-2">Current month Alternative Logs</small>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-4 col-md-6">
                        <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '15px', borderLeft: `6px solid ${styles.depositTheme}` }}>
                            <div className="card-body p-4 d-flex flex-column justify-content-center">
                                <h6 className="text-success text-uppercase mb-2 fw-bold">Last Month Deposit</h6>
                                <h3 className="fw-bold text-dark mb-0">৳ {stats.depositLastMonth.totalDeposit.toLocaleString()}</h3>
                                <small className="text-muted mt-2">Last month Alternative Logs</small>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-4 col-md-6">
                        <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '15px', borderLeft: `6px solid ${styles.depositTheme}` }}>
                            <div className="card-body p-4 d-flex flex-column justify-content-center">
                                <h6 className="text-success text-uppercase mb-2 fw-bold">Lifetime Deposit</h6>
                                <h3 className="fw-bold text-dark mb-0">৳ {stats.depositLifetime.totalDeposit.toLocaleString()}</h3>
                                <small className="text-muted mt-2">All time Alternative Logs</small>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Provider Breakdown Deposit */}
                <h5 className="mb-3 text-muted fw-bold text-uppercase">💳 Provider Breakdown (Deposit)</h5>
                <div className="row g-4 mb-5">
                    <div className="col-md-6">
                        <div className="card h-100 border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                            <div className="card-body p-3">
                                <div className="d-flex align-items-center mb-3 border-bottom pb-2">
                                    <img src={logos.bkash} alt="Bkash" style={{ width: '30px' }} className="me-2" />
                                    <h6 className="mb-0 fw-bold">Bkash Deposit Stats</h6>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span className="text-muted small">Today:</span>
                                    <span className="fw-bold text-dark">৳ {stats.depositToday.bkashTotal.toLocaleString()}</span>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span className="text-muted small">Yesterday:</span>
                                    <span className="fw-bold text-dark">৳ {stats.depositYesterday.bkashTotal.toLocaleString()}</span>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span className="text-muted small">Running:</span>
                                    <span className="fw-bold text-dark">৳ {stats.depositCurrentMonth.bkashTotal.toLocaleString()}</span>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span className="text-muted small">Last Month:</span>
                                    <span className="fw-bold text-dark">৳ {stats.depositLastMonth.bkashTotal.toLocaleString()}</span>
                                </div>
                                <div className="d-flex justify-content-between">
                                    <span className="text-muted small">Lifetime:</span>
                                    <span className="fw-bold" style={{ color: styles.bkashColor }}>৳ {stats.depositLifetime.bkashTotal.toLocaleString()}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="card h-100 border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                            <div className="card-body p-3">
                                <div className="d-flex align-items-center mb-3 border-bottom pb-2">
                                    <img src={logos.nagad} alt="Nagad" style={{ width: '30px' }} className="me-2" />
                                    <h6 className="mb-0 fw-bold">Nagad Deposit Stats</h6>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span className="text-muted small">Today:</span>
                                    <span className="fw-bold text-dark">৳ {stats.depositToday.nagadTotal.toLocaleString()}</span>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span className="text-muted small">Yesterday:</span>
                                    <span className="fw-bold text-dark">৳ {stats.depositYesterday.nagadTotal.toLocaleString()}</span>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span className="text-muted small">Running:</span>
                                    <span className="fw-bold text-dark">৳ {stats.depositCurrentMonth.nagadTotal.toLocaleString()}</span>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span className="text-muted small">Last Month:</span>
                                    <span className="fw-bold text-dark">৳ {stats.depositLastMonth.nagadTotal.toLocaleString()}</span>
                                </div>
                                <div className="d-flex justify-content-between">
                                    <span className="text-muted small">Lifetime:</span>
                                    <span className="fw-bold" style={{ color: styles.nagadColor }}>৳ {stats.depositLifetime.nagadTotal.toLocaleString()}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ======================= WITHDRAW OVERVIEW ======================= */}
                <h5 className="mb-3 text-muted fw-bold text-uppercase">💰 Withdraw Overview</h5>
                <div className="row g-4 mb-5">
                    <div className="col-lg-4 col-md-6">
                        <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '15px', borderLeft: '6px solid #0d6efd' }}>
                            <div className="card-body p-4 d-flex flex-column justify-content-center">
                                <h6 className="text-primary text-uppercase mb-2 fw-bold">Running Month</h6>
                                <h3 className="fw-bold text-dark mb-0">৳ {stats.currentMonth.totalWithdraw.toLocaleString()}</h3>
                                <small className="text-muted mt-2">Current month successful withdraw</small>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-4 col-md-6">
                        <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '15px', borderLeft: '6px solid #198754' }}>
                            <div className="card-body p-4 d-flex flex-column justify-content-center">
                                <h6 className="text-success text-uppercase mb-2 fw-bold">Last Month</h6>
                                <h3 className="fw-bold text-dark mb-0">৳ {stats.lastMonth.totalWithdraw.toLocaleString()}</h3>
                                <small className="text-muted mt-2">Last month successful withdraw</small>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-4 col-md-6">
                        <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '15px', borderLeft: '6px solid #212529' }}>
                            <div className="card-body p-4 d-flex flex-column justify-content-center">
                                <h6 className="text-secondary text-uppercase mb-2 fw-bold">Lifetime Total</h6>
                                <h3 className="fw-bold text-dark mb-0">৳ {stats.lifetime.totalWithdraw.toLocaleString()}</h3>
                                <small className="text-muted mt-2">All time successful withdraw</small>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Provider Breakdown Withdraw */}
                <h5 className="mb-3 text-muted fw-bold text-uppercase">💳 Provider Breakdown (Withdraw)</h5>
                
                <div className="row g-4 mb-5">
                    <div className="col-md-6">
                        <div className="card h-100 border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                            <div className="card-body p-3">
                                <div className="d-flex align-items-center mb-3 border-bottom pb-2">
                                    <img src={logos.bkash} alt="Bkash" style={{ width: '30px' }} className="me-2" />
                                    <h6 className="mb-0 fw-bold">Bkash Withdraw Stats</h6>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span className="text-muted small">Today:</span>
                                    <span className="fw-bold text-dark">৳ {stats.today.bkashTotal.toLocaleString()}</span>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span className="text-muted small">Yesterday:</span>
                                    <span className="fw-bold text-dark">৳ {stats.yesterday.bkashTotal.toLocaleString()}</span>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span className="text-muted small">Running:</span>
                                    <span className="fw-bold text-dark">৳ {stats.currentMonth.bkashTotal.toLocaleString()}</span>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span className="text-muted small">Last Month:</span>
                                    <span className="fw-bold text-dark">৳ {stats.lastMonth.bkashTotal.toLocaleString()}</span>
                                </div>
                                <div className="d-flex justify-content-between">
                                    <span className="text-muted small">Lifetime:</span>
                                    <span className="fw-bold" style={{ color: styles.bkashColor }}>৳ {stats.lifetime.bkashTotal.toLocaleString()}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="card h-100 border-0 shadow-sm" style={{ borderRadius: '12px' }}>
                            <div className="card-body p-3">
                                <div className="d-flex align-items-center mb-3 border-bottom pb-2">
                                    <img src={logos.nagad} alt="Nagad" style={{ width: '30px' }} className="me-2" />
                                    <h6 className="mb-0 fw-bold">Nagad Withdraw Stats</h6>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span className="text-muted small">Today:</span>
                                    <span className="fw-bold text-dark">৳ {stats.today.nagadTotal.toLocaleString()}</span>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span className="text-muted small">Yesterday:</span>
                                    <span className="fw-bold text-dark">৳ {stats.yesterday.nagadTotal.toLocaleString()}</span>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span className="text-muted small">Running:</span>
                                    <span className="fw-bold text-dark">৳ {stats.currentMonth.nagadTotal.toLocaleString()}</span>
                                </div>
                                <div className="d-flex justify-content-between mb-2">
                                    <span className="text-muted small">Last Month:</span>
                                    <span className="fw-bold text-dark">৳ {stats.lastMonth.nagadTotal.toLocaleString()}</span>
                                </div>
                                <div className="d-flex justify-content-between">
                                    <span className="text-muted small">Lifetime:</span>
                                    <span className="fw-bold" style={{ color: styles.nagadColor }}>৳ {stats.lifetime.nagadTotal.toLocaleString()}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </Layout>
    );
};

export default Dashboard;